'use client';

import React, { useState, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import UserSearchInput from './UserSearchInput';
import { isExternalRecipient } from '@/utils/emailDomain';

const ReassignmentModal = ({ isOpen, onClose, recipient, envelopeId, onReassign, isSubmitting, searchUsers, onViewDocuments }) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [justification, setJustification] = useState('');
  const [sendNotification, setSendNotification] = useState(true);
  const [confirmedExternal, setConfirmedExternal] = useState(false);
  const [errors, setErrors] = useState({});

  // Is the CURRENT recipient an outside party (patient / partner)? If so,
  // reassigning takes over their signing slot and they lose access entirely.
  // This fires on ~1% of reassignments - the other ~99% are routine
  // staff-to-staff coverage handoff and see no change to this form.
  const isExternal = isOpen && isExternalRecipient(recipient?.email);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedUser(null);
      setJustification('');
      setSendNotification(true);
      setConfirmedExternal(false);
      setErrors({});
    }
  }, [isOpen]);

  const validateForm = () => {
    const newErrors = {};
    if (!selectedUser) {
      newErrors.user = 'Please select a recipient from the directory';
    }
    if (!justification.trim()) {
      newErrors.justification = 'Justification is required';
    } else if (justification.trim().length < 10) {
      newErrors.justification = 'Justification must be at least 10 characters';
    }
    if (isExternal && !confirmedExternal) {
      newErrors.confirmedExternal = 'You must confirm you are taking over an outside recipient’s slot';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onReassign({
        newEmail: selectedUser.email,
        newName: selectedUser.displayName,
        justification: justification.trim(),
        sendNotification
      });
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Reassign Recipient</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-md">
              <p className="text-sm text-gray-600">Current recipient:</p>
              <p className="font-medium text-gray-900">{recipient?.name}</p>
              <p className="text-sm text-gray-600">{recipient?.email}</p>
              {isExternal && (
                <span className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded">
                  <AlertTriangle className="w-3 h-3" />
                  Outside recipient
                </span>
              )}
            </div>

            {isExternal && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md flex gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-700">
                  <p className="font-medium">This recipient is not a Spring Fertility address.</p>
                  <p className="mt-1">
                    This is usually a patient or their partner. Reassigning replaces their
                    signing slot with a colleague&apos;s, so they will no longer be able to open or
                    sign this envelope, and any signature still needed from them cannot be
                    collected.
                  </p>
                  <p className="mt-2">
                    <span className="font-medium">Only need to read or print the document?</span>{' '}
                    Close this and use{' '}
                    {onViewDocuments ? (
                      <button
                        type="button"
                        onClick={() => { onClose(); onViewDocuments(); }}
                        className="underline font-medium hover:text-amber-900"
                      >
                        View / Print
                      </button>
                    ) : (
                      <span className="font-medium">View / Print</span>
                    )}{' '}
                    instead — it does not change the envelope.
                  </p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Recipient *
                </label>
                <UserSearchInput
                  searchFn={searchUsers}
                  selectedUser={selectedUser}
                  onSelect={(user) => { setSelectedUser(user); setErrors(prev => ({ ...prev, user: undefined })); }}
                  onClear={() => setSelectedUser(null)}
                  disabled={isSubmitting}
                  error={errors.user}
                />
                {errors.user && <p className="text-red-500 text-sm mt-1">{errors.user}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Justification * <span className="text-gray-400 font-normal">(min 10 characters)</span>
                </label>
                <textarea
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  placeholder="Explain why this recipient needs to be reassigned..."
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-md ${errors.justification ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  disabled={isSubmitting}
                />
                {errors.justification && <p className="text-red-500 text-sm mt-1">{errors.justification}</p>}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="sendNotification"
                  checked={sendNotification}
                  onChange={(e) => setSendNotification(e.target.checked)}
                  className="mr-2"
                  disabled={isSubmitting}
                />
                <label htmlFor="sendNotification" className="text-sm text-gray-700">
                  Send notification to new recipient
                </label>
              </div>

              {isExternal && (
                <>
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="confirmExternalReassign"
                      checked={confirmedExternal}
                      onChange={(e) => {
                        setConfirmedExternal(e.target.checked);
                        setErrors(prev => ({ ...prev, confirmedExternal: undefined }));
                      }}
                      className="mr-2 mt-1"
                      disabled={isSubmitting}
                    />
                    <label
                      htmlFor="confirmExternalReassign"
                      className={`text-sm ${errors.confirmedExternal ? 'text-red-600' : 'text-gray-700'}`}
                    >
                      I understand this is an outside recipient and that reassigning will
                      remove their access to this envelope.
                    </label>
                  </div>
                  {errors.confirmedExternal && (
                    <p className="text-red-500 text-sm -mt-2">{errors.confirmedExternal}</p>
                  )}
                </>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={onClose}
                  className="flex-1 bg-gray-100 text-gray-700 hover:bg-gray-200"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#762dba] text-white hover:bg-[#3a1a6a]"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Reassigning...' : 'Reassign'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReassignmentModal;
